import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FunnelIcon, ListBulletIcon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import { MoreMenu } from '@redpanda/ui';
import { isAdmin } from '@redpanda/utils';
import { Role } from '@redpanda/types';
import { UserContext } from '../context/UserContext';

/**
 * 商機列表／看板共用的檢視列（docs/介面優化.md R24）
 * - 「列表／看板」分段切換：目前所在的檢視用主色淡底標示
 * - 「銷售漏斗」是檢視選項（開關），不是動作按鈕
 * - 版面：搜尋／篩選（children，列表頁用）在左，與其他列表頁的搜尋框位置一致；檢視切換靠右，列表、看板兩頁位置相同
 * - 與標題列間距 mt-4、控制項一律 h-10：和其他列表頁「標題列 mb-4 → 搜尋列」的位置完全相同，切換頁面不跳動
 */
export const OpportunityViewTabs: React.FC<{
  current: 'list' | 'kanban';
  showFunnel: boolean;
  onToggleFunnel: () => void;
  children?: React.ReactNode;
}> = ({ current, showFunnel, onToggleFunnel, children }) => {
  const { t } = useTranslation();
  const tabClass = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm transition-colors ${
      active ? 'bg-accent-soft text-accent font-medium' : 'text-fg-secondary hover:text-fg'
    }`;
  const views = [
    { key: 'list', to: '/opportunities', label: t('opportunities.views.list', { ns: 'pages' }), Icon: ListBulletIcon },
    { key: 'kanban', to: '/opportunities/kanban', label: t('opportunities.views.kanban', { ns: 'pages' }), Icon: ViewColumnsIcon },
  ] as const;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      {children && (
        <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
          {children}
        </div>
      )}
      <div className="flex items-center gap-3 ml-auto">
        <nav aria-label={t('opportunities.views.label', { ns: 'pages' })} className="inline-flex h-10 items-center rounded-md border border-line bg-surface p-0.5">
          {views.map(({ key, to, label, Icon }) => (
            <Link key={key} to={to} aria-current={current === key ? 'page' : undefined} className={tabClass(current === key)}>
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </Link>
          ))}
        </nav>
        <button
          type="button"
          onClick={onToggleFunnel}
          aria-pressed={showFunnel}
          className={`inline-flex items-center gap-1.5 h-10 rounded-md border px-3 text-sm transition-colors ${
            showFunnel ? 'border-accent bg-accent-soft text-accent font-medium' : 'border-line bg-surface text-fg-secondary hover:text-fg'
          }`}
        >
          <FunnelIcon className="h-4 w-4" aria-hidden="true" />
          {t('opportunities.views.funnel', { ns: 'pages' })}
        </button>
      </div>
    </div>
  );
};

/** 標題列「⋯」選單：放不常用的管理功能（目前只有階段管理，僅管理員可見） */
export const OpportunityMoreMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);

  if (!isAdmin(userContext?.user?.role as Role)) return null;

  return (
    <MoreMenu
      label={t('opportunities.views.more', { ns: 'pages' })}
      items={[
        { key: 'stages', label: t('opportunities.list.stageManagement', { ns: 'pages' }), onClick: () => navigate('/opportunities/stages') },
      ]}
    />
  );
};
